// The legacy SoundDialog: the rendered announcement, played in a dialog with a
// position slider and, on a desktop, a volume slider.

import { Fragment, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Slider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import VolumeDown from "@mui/icons-material/VolumeDown";
import VolumeMuteIcon from "@mui/icons-material/VolumeMute";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { useT } from "~/i18n";
import { fmtTime, parse } from "~/components/clock/dates";
import PauseIcon from "~/legacy/icons/PauseIcon";
import PlayIcon from "~/legacy/icons/PlayIcon";

const pad = (n: number) => String(n).padStart(2, "0");

/** "DD.MM. HH:mm", as moment formatted it for this dialog. */
function short(s: string | null | undefined): string {
  const d = parse(s ?? null) ?? (s ? new Date(s) : null);
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${fmtTime(d)}`;
}

const formatTime = (time: number) => `${pad(Math.floor(time / 60))}:${pad(Math.floor(time % 60))}`;

export function SoundDialog({
  url,
  message,
  startsAt,
  endsAt,
  noEnd,
  onClose,
}: {
  url: string | null;
  message: string;
  startsAt: string | null | undefined;
  endsAt: string | null | undefined;
  noEnd: boolean;
  onClose: () => void;
}) {
  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useT();

  const audio = useRef<HTMLAudioElement | null>(null);
  const [iconColor, setIconColor] = useState(theme.palette.text.primary);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);

  useEffect(() => {
    setIconColor(theme.palette.text.primary);
  }, [theme.palette.text.primary]);

  useEffect(() => {
    if (!url) return;
    // A fresh query string, so that a second rendering of the same window is
    // not answered from the cache — the legacy ?timestamp=.
    const a = new Audio(`${url}${url.includes("?") ? "&" : "?"}timestamp=${Date.now()}`);
    audio.current = a;
    a.volume = 0.5;
    a.addEventListener("loadedmetadata", () => setDuration(Math.trunc(a.duration)));
    a.addEventListener("loadeddata", () => {
      void a.play().catch(() => {});
      setPlaying(true);
    });
    a.addEventListener("timeupdate", () => setCurrentTime(Math.trunc(a.currentTime)));
    a.addEventListener("ended", () => setPlaying(false));
    a.load();
    return () => {
      a.pause();
      audio.current = null;
      setPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setVolume(0.5);
    };
  }, [url]);

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (playing) a.pause();
    else void a.play().catch(() => {});
    setPlaying(!playing);
  };

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const v = value as number;
    setCurrentTime(v);
    if (audio.current) audio.current.currentTime = v;
  };

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const v = value as number;
    setVolume(v);
    if (audio.current) audio.current.volume = v;
  };

  const setVol = (v: number) => {
    setVolume(v);
    if (audio.current) audio.current.volume = v;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={!!url}
      onKeyDown={(e) => {
        if (e.key === " ") toggle();
      }}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          height: 275,
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "center", px: 1 }}>
        <Box sx={{ borderBottom: 2, borderBottomColor: "divider", width: "100%" }}>
          <Typography noWrap sx={{ fontSize: 18, fontWeight: "bold", ml: 1 }}>
            {message || t("no-title")}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14, ml: 1, mb: 0.5 }}>
            {`${short(startsAt)} - ${noEnd ? t("no-enddate") : short(endsAt)}`}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ py: 0, pl: belowsm ? 1 : 7.5 }}>
        {belowsm ? (
          <Fragment>
            <Grid container>
              <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                <Typography sx={{ mt: 5 }}>{formatTime(currentTime)}</Typography>
              </Grid>
              <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                <Grid item>
                  <IconButton
                    onClick={toggle}
                    onMouseEnter={() => setIconColor(theme.palette.primary.main)}
                    onMouseLeave={() => setIconColor(theme.palette.text.primary)}
                    sx={{ border: 2, p: 1.5, mt: 1, borderColor: iconColor }}
                  >
                    {playing ? <PauseIcon color={iconColor} fontSize={28} /> : <PlayIcon color={iconColor} fontSize={28} />}
                  </IconButton>
                </Grid>
              </Grid>
              <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                <Typography sx={{ mt: 5 }}>{formatTime(duration)}</Typography>
              </Grid>
            </Grid>
            <Grid container sx={{ justifyContent: "center", mt: 1 }}>
              <Grid item xs={10}>
                <Slider
                  value={Math.trunc(currentTime)}
                  onChange={handleSliderChange}
                  size="medium"
                  step={1}
                  min={0}
                  max={Math.trunc(duration)}
                  sx={{ color: "text.primary", minWidth: 200, width: "100%", "&:hover": { color: "primary.main" } }}
                />
              </Grid>
            </Grid>
          </Fragment>
        ) : (
          <Fragment>
            <Grid container sx={{ pl: 1 }}>
              <Grid item>
                <IconButton
                  onClick={toggle}
                  onMouseEnter={() => setIconColor(theme.palette.primary.main)}
                  onMouseLeave={() => setIconColor(theme.palette.text.primary)}
                  sx={{ m: 1, ml: 2 }}
                >
                  {playing ? <PauseIcon color={iconColor} fontSize={26} /> : <PlayIcon color={iconColor} fontSize={26} />}
                </IconButton>
              </Grid>
              <Grid item sx={{ pt: 1, pl: 1 }}>
                <Typography sx={{ mt: 1.5, mr: 1 }}>{formatTime(currentTime)}</Typography>
              </Grid>
              <Grid item sm={5} xs={8} sx={{ pt: 2 }}>
                <Slider
                  value={Math.trunc(currentTime)}
                  onChange={handleSliderChange}
                  size="small"
                  step={1}
                  min={0}
                  max={Math.trunc(duration)}
                  sx={{ color: "text.primary", mx: 1, "&:hover": { color: "primary.main" } }}
                />
              </Grid>
              <Grid item sx={{ pt: 1, pl: 2 }}>
                <Typography sx={{ mt: 1.5, ml: 1 }}>{formatTime(duration)}</Typography>
              </Grid>
            </Grid>
            <Grid container sx={{ pl: 3.2 }}>
              <IconButton onClick={() => setVol(volume !== 0 ? 0 : 0.5)}>
                {volume === 0 ? (
                  <VolumeMuteIcon sx={{ color: "text.primary", fontSize: 26, "&:hover": { color: "primary.main" } }} />
                ) : (
                  <VolumeDown sx={{ color: "text.primary", fontSize: 26, "&:hover": { color: "primary.main" } }} />
                )}
              </IconButton>
              <Slider
                value={volume}
                onChange={handleVolumeChange}
                step={0.01}
                min={0}
                max={1}
                size="small"
                sx={{ color: "text.primary", width: "55%", m: 1, pl: 0.5, pt: 2, "&:hover": { color: "primary.main" } }}
              />
              <IconButton onClick={() => setVol(1)} sx={{ ml: 1 }}>
                <VolumeUpIcon sx={{ color: "text.primary", fontSize: 26, "&:hover": { color: "primary.main" } }} />
              </IconButton>
            </Grid>
          </Fragment>
        )}
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
        <Grid container sx={{ width: "100%", borderTop: 2, borderColor: "divider" }}>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              mt: 1,
              mr: 1,
              width: belowsm ? "40%" : "auto",
              backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
              textTransform: "none",
              border: theme.palette.mode === "dark" ? 1 : 0,
              borderColor: "primary.main",
              borderRadius: 6,
              "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main" },
            }}
          >
            {t("close")}
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
